/**
 * renderMentionText
 * Parses `@[ProductName](productId)` syntax in a string and returns
 * an array of React-compatible elements (strings and Link nodes).
 *
 * Usage (inside a component):
 *   import { renderMentionText } from "../../utils/renderMentionText";
 *   ...
 *   <p>{renderMentionText(comment.text)}</p>
 */

import React from "react";
import { Link } from "react-router-dom";

const MENTION_PATTERN = /@\[([^\]]+)\]\(([^)]+)\)/g;

export function isMongoObjectId(str) {
  return typeof str === "string" && /^[0-9a-fA-F]{24}$/.test(str);
}

/** Visible token shown in the textarea while composing (e.g. "@Galaxy S24") */
export function buildMentionDisplayToken(productName) {
  return `@${productName}`;
}

/** Parse all `@[Name](id)` tokens from stored text. */
export function parseStoredMentions(text) {
  if (!text || typeof text !== "string") return [];

  const mentions = [];
  let match;

  MENTION_PATTERN.lastIndex = 0;
  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    mentions.push({
      full: match[0],
      name: match[1],
      id: match[2],
      index: match.index,
    });
  }

  return mentions;
}

/** Product IDs whose stored mention label is an ObjectId instead of a name. */
export function collectMentionIdsNeedingResolution(text) {
  return [
    ...new Set(
      parseStoredMentions(text)
        .filter((m) => isMongoObjectId(m.name) || m.name === m.id)
        .map((m) => m.id),
    ),
  ];
}

/**
 * Convert stored `@[Name](id)` mentions to friendly `@Name` display tokens
 * for textarea editing. Returns a registry map for re-serialization on save.
 */
export function storedMentionsToDisplayText(text, nameById = {}) {
  if (!text || typeof text !== "string" || !text.includes("@[")) {
    return { displayText: text || "", registry: new Map() };
  }

  const mentions = parseStoredMentions(text);
  if (mentions.length === 0) {
    return { displayText: text, registry: new Map() };
  }

  const registry = new Map();
  const sorted = [...mentions].sort((a, b) => b.index - a.index);
  let displayText = text;

  for (const { full, name, id, index } of sorted) {
    const resolvedName =
      isMongoObjectId(name) || name === id ? nameById[id] || name : name;
    const displayToken = buildMentionDisplayToken(resolvedName);
    registry.set(displayToken, { id, name: resolvedName });
    displayText =
      displayText.slice(0, index) +
      displayToken +
      displayText.slice(index + full.length);
  }

  return { displayText, registry };
}

/**
 * Convert display tokens to stored format @[Name](id) before sending to the API.
 * @param {string} text
 * @param {Map<string, { id: string, name: string }>} registry
 */
export function serializeMentionsForSubmit(text, registry) {
  if (!text || !registry || registry.size === 0) return text;

  let result = text;
  const tokens = [...registry.keys()].sort((a, b) => b.length - a.length);

  for (const token of tokens) {
    if (!result.includes(token)) continue;
    const { id, name } = registry.get(token);
    const stored = `@[${name}](${id})`;
    result = result.split(token).join(stored);
  }

  return result;
}

export function renderMentionText(text) {
  if (!text || typeof text !== "string") return text;
  if (!text.includes("@[")) return text;

  const parts = [];
  let lastIndex = 0;
  let match;

  MENTION_PATTERN.lastIndex = 0;
  while ((match = MENTION_PATTERN.exec(text)) !== null) {
    const [full, name, id] = match;
    const start = match.index;

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    parts.push(
      <Link
        key={`mention-${id}-${start}`}
        to={`/product/${id}`}
        style={{
          color: "var(--color-primary)",
          fontWeight: 600,
          textDecoration: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        @{name}
      </Link>
    );

    lastIndex = start + full.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
}
